import _ from 'lodash'
import { get } from 'lodash'
import { first, take } from 'rxjs/operators'
import blobStream from 'blob-stream'
import Konva from 'konva'
import PDFDocument from "pdfkit/js/pdfkit.standalone.js"
import { bytesToBase64 } from "byte-base64"

import { SearchFeatured, getTranslatedString, DefectAlteration, DefectElement, ConditionReport, Alteration, Wemi, CounterSignature } from '@skinsoft/types'
import { thesaurusTermSelectors, conditionReportSelectors } from '@skinsoft/selectors'
import { translatedTitle } from '@skinsoft/selectors/alias-selectors'
import { AliasDataStore, ThesaurusTermDataStore } from '@skinsoft/entity'
import { isImageOrientationAutoManaged, getReadableDate, DATE_FORMATS, toFormat } from '@skinsoft/utils'
import { isWemi } from '@skinsoft/wemi-utils'
import { bus } from '@skinsoft/bus'
import { BASE_64_JPEG_START, extractAndRemoveExifOrientationFromMedia, getImageOrientation, ImageTransferHelper } from '@skinsoft/media-transfer-helper'

import { DrawingToolType } from '@skinsoft/entity-ui/src/condition-report/drawing-toolbox'
import { getLabelCoordinates } from '@skinsoft/entity-ui/src/condition-report/drawing-toolbox/label-coordinates-helper'
import './pdf-document-extension-methods'
import { toSearchFeatured$ } from '@skinsoft/search-data-store/src/featured'

declare const FALLBACK_LOCALE: string

enum AlterationsDisplayMode {
    SUMMARY,
    WITH_THUMBNAILS,
    FULL_PAGE
}

export interface SignatureDrawing {
    canvas: { width: number, height: number },
    signature: any[]
}

interface ConstructorOptions {
    topicToEmitProgress: string,
    defaultSignatrueCanvasWidth: number,
    defaultSignatrueCanvasHeight: number,
    i18nMessages,
    i18nDatesTimeFormat
}

export class ConditionReportPDFGenerator {

    // Layout constants
    public static readonly TITLE_FONT_SIZE = 19;
    public static readonly SECTION_FONT_SIZE = 16;
    public static readonly BODY_TEXT_FONT_SIZE = 11;
    public static readonly FOOTER_FONT_SIZE = 10;
    public static readonly DOCUMENT_MARGIN = 35;
    public static readonly A4_PAPER_SIZE = [595.28, 841.89];
    public static readonly DEFAULT_IMAGE_SIZE = 160;
    public static readonly FULL_PAGE_IMAGE_SIZE = 400;
    public static readonly ALTERATION_IMAGE_SIZE = 450
    public static readonly IMAGE_BETWEEN_GAP = 15;
    public static readonly IMAGE_TEXT_BETWEEN_GAP = 30;
    public static readonly ALTERATION_TEXT_BETWEEN_COL_GAP = 30
    public static readonly SIGNATURE_IMAGE_WIDTH = 250;
    public static readonly IMAGE_GENERATION_TARGET_PIXEL_NUMBER = 1500000;
    public static readonly SIGNATURE_GENERATION_PIXEL_RATIO = 0.5;
    public static SIGNATURE_IMAGE_HEIGHT

    public static readonly REGULAR_FONT: string = "Helvetica";
    public static readonly BOLD_FONT: string = "Helvetica-Bold";

    private hiddenCanvasContainer: HTMLDivElement = null as any;
    private hiddenStage: Konva.Stage = null as any;
    private hiddenStageShapesLayer: Konva.Layer = null as any;
    private hiddenStageAnnotationsLayer: Konva.Layer = null as any;

    // For annotations, see their equivalents in the drawing editor
    private static readonly STROKE_WIDTH_RATIO: number = 0.01;
    private static readonly ANNOTATION_LABEL_RADIUS_RATIO = 0.04;
    private static readonly ANNOTATION_LABEL_FONT_SIZE_RATIO = 0.04;
    private static readonly ANNOTATION_LABEL_STROKE_WIDTH_RATIO = 0.004;

    private static I18N_MESSAGES
    private static I18N_DATES_TIME_FORMAT
    private static locale = FALLBACK_LOCALE
    private excludedMediaIds: string[] = []

    private pageNumber: number = 0;
    private static TOPIC_TO_EMIT_PROGRESS: string | null

    public static getInstance() {
        if (!ConditionReportPDFGenerator.instance) {
            ConditionReportPDFGenerator.instance = new ConditionReportPDFGenerator()
        }

        return ConditionReportPDFGenerator.instance
    }

    public static install(Vue, options: ConstructorOptions) {
        this.I18N_MESSAGES = options.i18nMessages
        this.I18N_DATES_TIME_FORMAT = options.i18nDatesTimeFormat
        this.TOPIC_TO_EMIT_PROGRESS = options.topicToEmitProgress
        this.SIGNATURE_IMAGE_HEIGHT = ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH *
            options.defaultSignatrueCanvasHeight / options.defaultSignatrueCanvasWidth;

        Vue.prototype.$conditionReportPDFGenerator = ConditionReportPDFGenerator.getInstance()
    }

    private static instance: ConditionReportPDFGenerator = null as any

    // tslint:disable-next-line:member-ordering
    public generatePDF(conditionReport: ConditionReport, wemi: Wemi, onPdfGenerated, locale?: string, excludedMediaIds?: string[])
    public generatePDF(conditionReport: ConditionReport, searchFeatured: SearchFeatured, onPdfGenerated, locale?: string, excludedMediaIds?: string[])
    public generatePDF(conditionReport: ConditionReport, associatedItem: SearchFeatured | Wemi, onPdfGenerated, locale?: string, excludedMediaIds?: string[]) {
        
        ConditionReportPDFGenerator.locale = locale ? locale : FALLBACK_LOCALE
        this.excludedMediaIds = excludedMediaIds?.length ? excludedMediaIds : []

        return new Promise<void>((resolve, reject) => {
            this.createHiddenCanvas();
    
            const doc = new PDFDocument({
                bufferPages: true,
                margin: ConditionReportPDFGenerator.DOCUMENT_MARGIN,
                size: ConditionReportPDFGenerator.A4_PAPER_SIZE,
            });
    
            const stream = doc.pipe(blobStream());
            try {
                if(isWemi(associatedItem)) {
                    toSearchFeatured$(associatedItem as Wemi, ConditionReportPDFGenerator.locale).toPromise().then((searchFeatured: SearchFeatured) => {
                        this.drawPDF(doc, conditionReport, searchFeatured)
                            .catch(err => {
                                reject(err)
                            })
                    })
                } else {
                    this.drawPDF(doc, conditionReport, associatedItem as SearchFeatured)
                        .catch(err => {
                            reject(err)
                        })
                }
            } catch(err) {
                reject(err)
            }
            
            stream.on('finish', () => {
                const blob = stream.toBlob('application/pdf');
    
                // @TODO manage cache
                // const cache = new IndexedDBCache();
                // cache.write(conditionReport.id, blob)
                onPdfGenerated(blob)
                resolve()
            });
        })
        
    }

    private  addFooters(doc, conditionReport: ConditionReport) {
        const bottom = doc.page.margins.bottom;
        const footerPrefix = this.$t('pdfDocument.footer.prefix')
        const footerSeparator = this.$t('pdfDocument.footer.separator')
        const range = doc.bufferedPageRange();

        for (let pageIndex = range.start; pageIndex < range.count; pageIndex++) {
            doc.switchToPage(pageIndex)
            // cancelling margin to be able to write into it
            doc.page.margins.bottom = 0;
            const pageNumber = pageIndex + 1;
            doc
                .fill("black")
                .font(ConditionReportPDFGenerator.REGULAR_FONT)
                .fontSize(ConditionReportPDFGenerator.FOOTER_FONT_SIZE)
                .text(`${footerPrefix} ${conditionReportSelectors.referenceNumber(conditionReport)} ${footerSeparator} ${pageNumber}/${range.count}`, 20, doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN, {
                    align: 'right',
                });
            // restoring margin
            doc.page.margins.bottom = bottom;
        }
    }

    /**
     * Creates a unique hidden canvas to perform drawing operations
     * before exporting images to pdf.
     */
    private  createHiddenCanvas() {
        if (!this.hiddenCanvasContainer) {
            this.hiddenCanvasContainer = document.createElement("div");
            this.hiddenCanvasContainer.id = 'container';
            this.hiddenCanvasContainer.hidden = true;
            document.body.appendChild(this.hiddenCanvasContainer);

            // Creating konva stage to render image
            const stageConfig = {
                container: 'container',
                height: 50,
                width: 50,
            }

            this.hiddenStage = new Konva.Stage(stageConfig);
            this.hiddenStageShapesLayer = new Konva.Layer({ listening: false })
            this.hiddenStageAnnotationsLayer = new Konva.Layer({ listening: false })
            this.hiddenStage.add(this.hiddenStageShapesLayer)
            this.hiddenStage.add(this.hiddenStageAnnotationsLayer)
        }
    }

    private  resetHiddenCanvases() {
        this.hiddenStageShapesLayer.destroyChildren()
        this.hiddenStageAnnotationsLayer.destroyChildren()
    }

    private  async drawPDF(doc, conditionReport: ConditionReport, searchFeatured: SearchFeatured) {

        this.createDocumentHeader(doc, conditionReportSelectors.referenceNumber(conditionReport) as string)

        await this.createContextSection(doc, conditionReport)
        if(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS) {
            bus.emit(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS, { step: 2 })
        }

        await this.createItemSection(doc, searchFeatured)
        if(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS) {
            bus.emit(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS, { step: 3 })
        }
        
        await this.createNewAlterationsSection(doc, conditionReport)
        if(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS) {
            bus.emit(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS, { step: 4 })
        }
        
        await this.createReportSection(doc, conditionReport);
        if(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS) {
            bus.emit(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS, { step: 5 })
        }

        /*const defectElements = defects(conditionReport)
        if (this.shallGenerateAlterationsSection(defectElements)) {
            await this.createAlterationsSection(doc, defectElements);
            await this.createDetailedAlterationsSection(doc, defectElements)
        }
        if(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS) {
            bus.emit(ConditionReportPDFGenerator.TOPIC_TO_EMIT_PROGRESS, { step: 6 })
        }*/

        this.addFooters(doc, conditionReport);
        doc.end();
    }

    private  async createDocumentHeader(doc, conditionReportTitle) {
        doc.moveDown(8);
        doc.font(ConditionReportPDFGenerator.BOLD_FONT)
        const titlePrefix = this.$t('pdfDocument.mainTitlePrefix')

        doc.fontSize(ConditionReportPDFGenerator.TITLE_FONT_SIZE).text(`${titlePrefix}${conditionReportTitle}`, {
            align: 'center'
        });
    }

    private  async createItemSection(doc, searchFeatured: SearchFeatured) {
        doc.addPage()
        let indentation = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
        let textWidth = doc.page.width - 2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN;

        doc.sectionTitle(this.$t('pdfDocument.objectSection.sectionTitle'));
        doc.moveDown(2)
        const yImage = doc.y;
        if (searchFeatured.media) {
            indentation = ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE + ConditionReportPDFGenerator.IMAGE_TEXT_BETWEEN_GAP + ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            textWidth = doc.page.width - indentation - ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            await this.addImageFromMediaId(doc, searchFeatured.media)
        }

        let alias: string = searchFeatured.credits ? searchFeatured.credits.alias : "";
        const authorFunction: string = searchFeatured.credits ? searchFeatured.credits.authorFunction : "";
        if (alias) {
            alias = getTranslatedString(translatedTitle(await AliasDataStore.getInstance().fetchOne(alias).pipe(first()).toPromise()), ConditionReportPDFGenerator.locale)
        }
        const authorSummary = (alias || "") + (alias && authorFunction ? ` (${authorFunction})` : "")

        doc.y = yImage;
        doc.x = indentation;
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.inventoryNumber'), searchFeatured.inventoryNumber, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.title'), searchFeatured.title, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.author'), authorSummary, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.creationDate'), searchFeatured.creation_date, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.typology'), searchFeatured.domain, { width: textWidth })
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.denomination'), searchFeatured.denomination, { width: textWidth })
        if (searchFeatured.materialAndTechniques && searchFeatured.materialAndTechniques.length > 0) {
            doc.boldText(this.$t('pdfDocument.objectSection.materialsAndTechniques'), { width: textWidth })
            for (const mt of searchFeatured.materialAndTechniques) {
                doc.regularText(`- ${mt}`, { width: textWidth, indent: 10, paragraphGap: 8 })
            }
        }
        if (searchFeatured.measurements && searchFeatured.measurements.length > 0) {
            doc.boldText(this.$t('pdfDocument.objectSection.dimensions'), { width: textWidth })
            for (const measurement of searchFeatured.measurements) {
                doc.regularText(`- ${measurement}`, { width: textWidth, indent: 10 })
            }

        }
        doc.moveDown()
        doc.dataWithBoldLabel(this.$t('pdfDocument.objectSection.institution'), searchFeatured.institution, { width: textWidth })
    }

    private  async createContextSection(doc, conditionReport: ConditionReport) {
        doc.sectionTitle(this.$t('pdfDocument.contextSection.sectionTitle'))

        // Medias
        doc.moveDown(1);
        const associatedMedias = conditionReportSelectors.medias(conditionReport)
                                    .filter(mediaId => !this.excludedMediaIds.includes(mediaId))



        await this.addImageBlock(doc, associatedMedias)

        // Report type
        const resolvedReportType = await this.getThesaurusTermFromId(conditionReportSelectors.reportType(conditionReport))
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportType'), resolvedReportType)

        // Report type
        const resolvedReportReason = await this.getThesaurusTermFromId(conditionReportSelectors.reportReason(conditionReport))
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportReason'), resolvedReportReason)

        // Report date
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportDate'), getReadableDate(conditionReportSelectors.date(conditionReport)))

        // Report location
        doc.dataWithBoldLabel(this.$t('pdfDocument.contextSection.reportPlace'), conditionReportSelectors.location(conditionReport, ConditionReportPDFGenerator.locale))
    }

    private async createNewAlterationsSection(doc, conditionReport: ConditionReport) {
        if(conditionReport.conditionReportCS.alterations) {
            const mediaReferencedInAlterations = _.uniq(conditionReport.conditionReportCS.alterations
                .map(alteration => alteration.media)
                .filter(media => media != null))

            const identation = ConditionReportPDFGenerator.DOCUMENT_MARGIN
            const textWidth = doc.page.width - identation - ConditionReportPDFGenerator.DOCUMENT_MARGIN

            for(const mediaId of mediaReferencedInAlterations) {
                if(mediaId) {
                    doc.addPage()
                    doc.sectionTitle(this.$t('pdfDocument.alterationsSection.sectionTitle'))
                    doc.moveDown(1)

                    const alterationsLinkedToMedia = conditionReport.conditionReportCS.alterations.filter(alteration => alteration.media === mediaId)
                    await this.drawAlterationOnImage(doc, alterationsLinkedToMedia, mediaId)

                    doc.x = identation
                    doc.y += ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE
                    doc.moveDown(1)

                    let heightOfAlterationTextArea = doc.page.height - doc.y - ConditionReportPDFGenerator.DOCUMENT_MARGIN
                    let totalTextSizeTillNow = 0

                    for(const [index, alteration] of alterationsLinkedToMedia.entries()) {
                        const typeOfAlteration = alteration.typeOfAlteration ? await ThesaurusTermDataStore.getInstance().fetchOne(alteration.typeOfAlteration).toPromise() : undefined
                        const typeOfAlterationText = typeOfAlteration ? getTranslatedString(
                            thesaurusTermSelectors.preferredTerm(typeOfAlteration),
                            ConditionReportPDFGenerator.locale
                        ) : undefined
                        const title = alteration.title ? getTranslatedString(alteration.title, ConditionReportPDFGenerator.locale) : undefined
                        const precision = alteration.precision ? getTranslatedString(alteration.precision, ConditionReportPDFGenerator.locale) : undefined

                        let boldLine = `${index+1}.`
                        if(typeOfAlteration || title) {
                            boldLine += ` ${title ? title : ''} - ${typeOfAlterationText ? typeOfAlterationText : ''}`
                        }

                        const textOptions: any = {
                            width: textWidth,
                            align: 'justify',
                        }
                        const boldTextOptions: any = {
                            width: textWidth,
                            align: 'justify',
                        }
                        let boldLineHeight = doc.heightOfString(boldLine, boldTextOptions)
                        let precisionHeight = doc.heightOfString(precision, textOptions)
                        let alterationTextHeight = boldLineHeight + precisionHeight

                        totalTextSizeTillNow += alterationTextHeight
                        
                        if(totalTextSizeTillNow > heightOfAlterationTextArea) {
                            // we reached the bottom, we need to go to a new page
                            doc.addPage()
                            doc.sectionTitle(this.$t('pdfDocument.alterationsSection.sectionTitle'))
                            doc.moveDown(1)
                            await this.drawAlterationOnImage(doc, alterationsLinkedToMedia, mediaId)
                            doc.x = identation
                            doc.y += ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE
                            doc.moveDown(1)

                            /*if(alterationTextHeight > heightOfAlterationTextArea) {
                                // Handle the case where the text is longer than one page
                            }*/
                        }
                        
                        doc.boldText(boldLine, boldTextOptions)
                        doc.regularText(precision, textOptions)
                        doc.moveDown(1)
                    }
                }
            }
        }
    }

    private  async createAlterationsSection(doc, defectElements: DefectElement[]) {
        doc.addPage()
        doc.sectionTitle(this.$t('pdfDocument.alterationsSection.sectionTitle'))

        await this.generateAlterationSection(doc, defectElements, AlterationsDisplayMode.SUMMARY)
        doc.moveUp()
        doc.softSeparationLine()
        await this.generateAlterationSection(doc, defectElements, AlterationsDisplayMode.WITH_THUMBNAILS)
    }

    private  async createDetailedAlterationsSection(doc, defectElements: DefectElement[]) {
        await this.generateAlterationSection(doc, defectElements, AlterationsDisplayMode.FULL_PAGE)
    }

    private  async createReportSection(doc, conditionReport: ConditionReport) {
        doc.addPage()
        doc.sectionTitle(this.$t('pdfDocument.reportSection.sectionTitle'))

        doc.moveDown(1)
        const theComments = conditionReportSelectors.comments(conditionReport, ConditionReportPDFGenerator.locale)
        if (theComments) {
            doc.regularText(theComments, { align: "justify" })
        }

        doc.moveDown(1)
        const conservationStates = await conditionReportSelectors.stateOfConservation(conditionReport)
        doc.boldText(this.$t('pdfDocument.reportSection.stateOfConservation'));
        doc.moveDown(0.5)
        if (conservationStates) {
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN + 8;
            for (const state of conservationStates) {
                const stateTh = await this.getThesaurusTermFromId(state.stateOfConservation);
                doc.boldText(stateTh)
                doc.regularText(getTranslatedString(state.precision, ConditionReportPDFGenerator.locale), { align: "justify" })
                doc.moveDown(0.5)
            }
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
        }
        doc.moveDown()
        const priorityOfTreatment = await this.getThesaurusTermFromId(conditionReportSelectors.treatmentPriority(conditionReport))
        if (priorityOfTreatment) {
            doc.boldText(this.$t('pdfDocument.reportSection.treatmentPriority'), { continued: true });
            doc.regularText(priorityOfTreatment)
            doc.moveDown(1)
        }

        const theInterventionsToSchedule: any = await conditionReportSelectors.interventionsToSchedule(conditionReport)
        if (theInterventionsToSchedule) {
            doc.boldText(this.$t('pdfDocument.reportSection.interventionsToSchedule'));
            doc.moveDown(0.5)
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN + 8;
            for (const intervention of theInterventionsToSchedule) {
                const interventionType = await this.getThesaurusTermFromId(get(intervention, "typeIntervention"))
                if (interventionType) {
                    doc.boldText(interventionType)
                }
                doc.regularText(getTranslatedString(intervention.precision, ConditionReportPDFGenerator.locale), { align: "justify" })
                doc.moveDown(0.5)

            }
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;

            doc.moveDown(1)
        }
        doc.moveDown(3)
        doc.boldText(this.$t('pdfDocument.reportSection.signatoriesTitle'));
        doc.moveDown(1)
        // signatures
        const signers = get(conditionReport, 'conditionReportCS.signers')
        let lineY = doc.y;
        if (signers) {
            for (const signer of signers) {
                if (doc.y !== lineY) {
                    doc.moveDown(2);
                    lineY = doc.y;
                }
                const headerY = doc.y;
                let signerText = getTranslatedString(signer.signer, ConditionReportPDFGenerator.locale)
                if(signer.positionOfSignatory) {
                    const positionOfSignatory = await this.getThesaurusTermFromId(signer.positionOfSignatory)
                    signerText += ', ' + positionOfSignatory
                }
                doc
                    .fill('black')
                    .text(signerText, { width: ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH })

                if (signer.signature) {
                    const unMarshalledSignature = JSON.parse(signer.signature);
                    if (unMarshalledSignature
                        && unMarshalledSignature.signature
                        && unMarshalledSignature.signature.length > 0) {
                        this.generateSignature(doc, unMarshalledSignature, headerY)
                    } else {
                        this.generateSignature(doc, null, headerY)
                    }
                }
            }
            doc.moveDown(5) // to prevent next section to start on the signature
        }
        doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN

        this.createCounterSignatureSection(doc, conditionReport)
    }

    private async createCounterSignatureSection(doc, conditionReport: ConditionReport) {
        doc.moveDown(1)
        const counterSignatures:CounterSignature[] = get(conditionReport, 'conditionReportCS.counterSignatures', [])
        if (counterSignatures) {
            doc.moveDown(3)
            doc.sectionTitle(this.$t('pdfDocument.reportSection.counterSignaturesTitle'))
            doc.moveDown(1)

            for(const [index, counterSignature] of counterSignatures.entries()) {
                const estimatedHeight = 5
                if(doc.y + estimatedHeight > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN ) {
                    doc.addPage()
                }
                if(index != 0) {
                    this.drawLineSeparator(doc, ConditionReportPDFGenerator.DOCUMENT_MARGIN, 1)
                }

                let counterSignatureText = getTranslatedString(counterSignature.reason, ConditionReportPDFGenerator.locale)
                counterSignatureText += ', ' + getTranslatedString(counterSignature.location, ConditionReportPDFGenerator.locale)
                if(counterSignature.date) {
                    const dateFormat = this.getDateFormatFromServer()
                    counterSignatureText += ', ' + toFormat(counterSignature.date, dateFormat)
                }
                doc.fill('black')
                    .regularText(
                        counterSignatureText, 
                        { width: ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH }
                    )
                doc.moveDown(1)

                doc.dataWithBoldLabel(
                    this.$t('pdfDocument.reportSection.counterSignature.comment'), 
                    getTranslatedString(counterSignature.comment, ConditionReportPDFGenerator.locale), 
                    { width: doc.page.width - 2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN }
                )

                doc.moveDown(1)

                // counter signers
                doc.boldText(this.$t('pdfDocument.reportSection.signatoriesTitle'))
                if(counterSignature.observationCarriedPresenceOfs) {
                    let lineY = doc.y
                    for (const [index, signer] of counterSignature.observationCarriedPresenceOfs.entries()) {
                        if (doc.y !== lineY) {
                            const estimatedHeight2 = 7 + ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT
                            if(doc.y + estimatedHeight2 > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN ) {
                                doc.addPage()
                                doc.moveDown(0.1)
                            } else {
                                doc.moveDown(2);
                            }
                            lineY = doc.y;
                        }
                        const headerY = doc.y;
                        let signerText = getTranslatedString(signer.surnameFirstName, ConditionReportPDFGenerator.locale)
                        if(signer.function) {
                            const positionOfSignatory = getTranslatedString(signer.function, ConditionReportPDFGenerator.locale)
                            signerText += ', ' + positionOfSignatory
                        }
                        doc
                            .fill('black')
                            .regularText(signerText, { width: ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH })
        
                        if (signer.signature) {
                            const signature = getTranslatedString(signer.signature, 'fr')
                            if(signature) {
                                const unMarshalledSignature = JSON.parse(signature);
                                if (unMarshalledSignature
                                    && unMarshalledSignature.signature
                                    && unMarshalledSignature.signature.length > 0) {
                                    this.generateSignature(doc, unMarshalledSignature, headerY)
                                } else {
                                    this.generateSignature(doc, null, headerY)
                                }
                            }
                        }

                        if(index === counterSignature.observationCarriedPresenceOfs.length - 1 && index % 2 === 0) {
                            // if last signer is on first column we need to jump to next line
                            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN
                            doc.y += ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT
                            doc.moveDown(1)
                        }
                    }
                }
            }
        }
    }

    private  async addImageBlock(doc, mediasIds) {
        doc.moveDown(1)
        const imgStep = ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE + ConditionReportPDFGenerator.IMAGE_BETWEEN_GAP;
        for (const mediaId of mediasIds) {
            await this.addImageFromMediaId(doc, mediaId)

            doc.x += imgStep;

            // Continuing on a new line if needed
            if (doc.x + imgStep > doc.page.width - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
                doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                doc.y += imgStep;
            }

            // Continuing on a new page if needed
            if (doc.y + imgStep > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
                doc.addPage()
                doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                doc.y += ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            }
        }
        if (doc.x !== ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            doc.y += imgStep;
        }

    }

    private  async addImageFromMediaId(doc, mediaId, x?: number, y?: number, options?: any) {
        const saveY = doc.y;
        doc.save();
        const imgX = x ? x : doc.x;
        const imgY = y ? y : doc.y;

        const centerX = imgX + ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE / 2;
        const centerY = imgY + ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE / 2;

        const imgOptions = options ? options :
            {
                align: 'center',
                fit: [ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE, ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE],
                valign: 'center'
            };

        const isLocalMedia = mediaId.startsWith('local:')
        // avoid endlessly searching cached media id on API using the filename
        const mediaData = await ImageTransferHelper.getInstance().downloadImage(mediaId, undefined, isLocalMedia).pipe(take(1)).toPromise();
        
        let rotationAngle = 0
        const { orientation, mediaWithoutOrientation } = extractAndRemoveExifOrientationFromMedia(mediaData.dataUrl)
        let mediaBase64 = ''
        if(orientation !== -1) {
            if(typeof mediaWithoutOrientation === 'string' || mediaWithoutOrientation instanceof String) {
                mediaBase64 = mediaWithoutOrientation as string
            } else {
                mediaBase64 += BASE_64_JPEG_START + bytesToBase64(new Uint8Array(mediaWithoutOrientation))
            }

            switch (orientation) {
                case 3:
                    rotationAngle = 180
                    break;
                case 6:
                    rotationAngle = 90
                    break;
                case 8:
                    rotationAngle = -90
                    break;
            }
        } else {
            mediaBase64 = mediaWithoutOrientation as string
        }

        doc.rect(imgX, imgY, ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE, ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE).fill("lightgray");
        if (mediaBase64) {
            doc
                .rotate(rotationAngle, { origin: [centerX, centerY] })
                .image(mediaBase64, imgX, imgY, imgOptions)
        }
        doc.restore()
        doc.y = saveY;
    }

    private async getRotationAngleFromOrientation(imageDataUrl) {

        const img = new Image();
        img.src = imageDataUrl;
        const orientation = await getImageOrientation(img)
        switch (orientation) {
            case 3:
                return 180
            case 6:
                return 90
            case 8:
                return -90
            default:
                return 0
        }
    }

    private  generateSignature(doc, signatureDrawingData: SignatureDrawing | null, headerY) {

        if (signatureDrawingData) {
            // Resizing konva stage with signature format
            this.hiddenStage.width(signatureDrawingData.canvas.width)
            this.hiddenStage.height(signatureDrawingData.canvas.height)
            this.resetHiddenCanvases()
            signatureDrawingData.signature.map(signatureShape => {
                this.hiddenStageShapesLayer.add(new Konva.Line(signatureShape));
            })
            this.hiddenStage.add(this.hiddenStageShapesLayer)
            this.hiddenStageShapesLayer.draw();
            const signatureImgDataUrl = this.hiddenStage.toDataURL({ pixelRatio: ConditionReportPDFGenerator.SIGNATURE_GENERATION_PIXEL_RATIO });

            doc.image(signatureImgDataUrl, doc.x, doc.y, {
                align: 'center',
                height: ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT,
                valign: 'center',
                width: signatureDrawingData.canvas.width * ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT / signatureDrawingData.canvas.height,
            })
        }
        doc.x += ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH + ConditionReportPDFGenerator.IMAGE_BETWEEN_GAP
        doc.y = headerY;
        if (doc.x + ConditionReportPDFGenerator.SIGNATURE_IMAGE_WIDTH > doc.page.width - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {

            doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
            doc.y += ConditionReportPDFGenerator.SIGNATURE_IMAGE_HEIGHT + ConditionReportPDFGenerator.IMAGE_BETWEEN_GAP;
            doc.moveDown(1)
        }
    }

    private  async getThesaurusTermFromId(id): Promise<string> {
        if (id) {
            return getTranslatedString(thesaurusTermSelectors.preferredTerm(
                await ThesaurusTermDataStore.getInstance().fetchOne(id).pipe(first()).toPromise()
            ),
                ConditionReportPDFGenerator.locale)
        } else {
            return ""
        }
    }

    /**
     * Displays the alterations as a tree with images or as a summary, according to
     * 'withDetails' parameter
     * @param doc 
     * @param reportDefects 
     * @param displayMode
     */
    private  async generateAlterationSection(doc, reportDefects, displayMode: AlterationsDisplayMode = AlterationsDisplayMode.SUMMARY) {
        doc.moveDown(1)
        let firstPage: boolean = true;
        if (reportDefects) {
            // Looping over elements
            for (const element of reportDefects) {
                firstPage = false;
                doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                doc.fill("black")
                const elementName = getTranslatedString(element.element, ConditionReportPDFGenerator.locale);
                const elementPrecision = getTranslatedString(element.precision, ConditionReportPDFGenerator.locale)

                const elementSummary = (elementName || "") + (elementPrecision ? ` (${elementPrecision})` : "");

                const observations = element.observationAlterationDetails;
                if (displayMode !== AlterationsDisplayMode.FULL_PAGE) {
                    doc.boldText(elementSummary)
                }
                doc.moveDown(0.5)
                if (observations) {
                    for (const observation of observations) {
                        const materials = observation.materialAlterations;
                        if (materials) {
                            for (const material of materials) {
                                let typeOfObs = "";
                                let materialName = "";
                                if (observation.typeOfObservation) {
                                    typeOfObs = await this.getThesaurusTermFromId(observation.typeOfObservation)
                                }
                                if (material.material) {
                                    materialName = await this.getThesaurusTermFromId(material.material);
                                }
                                doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                                // const secondLevelDesc = `${typeOfObs || "..."} > ${materialName || "..."}`
                                const secondLevelDesc = typeOfObs + (typeOfObs && materialName ? " > " : "") + materialName
                                doc.breakContinue()
                                doc.moveDown()

                                switch (displayMode) {
                                    case AlterationsDisplayMode.WITH_THUMBNAILS:
                                        if (secondLevelDesc) {
                                            doc.regularText(secondLevelDesc, { indent: 0 })
                                            doc.moveDown()
                                        }
                                        break;
                                    case AlterationsDisplayMode.SUMMARY:
                                        if (secondLevelDesc) {
                                            doc.regularText(`- ${secondLevelDesc} : `, { indent: 8, continued: true })
                                        }
                                        break;
                                }
                                if (material.alterations) {
                                    let firstAlteration = true;
                                    for (const alteration of material.alterations) {
                                        if (displayMode !== AlterationsDisplayMode.FULL_PAGE) {
                                            this.maybeOnANewPage(alteration, doc);
                                        }
                                        doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                                        let typeOfAlteration: string = "";
                                        if (alteration.typeOfAlteration) {
                                            typeOfAlteration = await this.getThesaurusTermFromId(alteration.typeOfAlteration);
                                        }
                                        const alterationPrecision: string = getTranslatedString(alteration.precision, ConditionReportPDFGenerator.locale);

                                        if (displayMode !== AlterationsDisplayMode.FULL_PAGE) {
                                            const withDetails = displayMode === AlterationsDisplayMode.WITH_THUMBNAILS;
                                            doc
                                                .fill("#f15f22")
                                                .boldText(`${firstAlteration || withDetails ? "" : ","}  ${typeOfAlteration}`, { continued: !!alterationPrecision || !withDetails })
                                        }
                                        if (alterationPrecision) {
                                            switch (displayMode) {
                                                case AlterationsDisplayMode.SUMMARY:
                                                    doc
                                                        .fill("black")
                                                        .regularText(" (" + alterationPrecision + ")", { continued: true })
                                                    break;
                                                case AlterationsDisplayMode.WITH_THUMBNAILS:
                                                    doc
                                                        .fill("black")
                                                        .regularText(" (" + alterationPrecision + ")")
                                                    break;
                                            }
                                        }

                                        if (displayMode === AlterationsDisplayMode.WITH_THUMBNAILS ||
                                            displayMode === AlterationsDisplayMode.FULL_PAGE) {
                                            await this.drawAnnotations(doc, alteration.annotations, displayMode, {
                                                elementSummary,
                                                typeOfObs,
                                                // tslint:disable-next-line:object-literal-sort-keys
                                                materialName,
                                                typeOfAlteration,
                                                alterationPrecision
                                            })
                                            doc.moveDown(1)
                                        }
                                        firstAlteration = false;
                                    }
                                    doc.breakContinue()
                                    doc.moveDown()
                                }
                                doc.moveDown()
                            }
                        }
                    }
                }
                doc.moveDown(1)
            }
        }
        doc.moveDown(1)
    }

    private async drawAlterationOnImage(doc, alterations: Alteration[], mediaId: string) {
        this.resetHiddenCanvases()

        // Retrieving image data
        const media = await ImageTransferHelper.getInstance().downloadImage(mediaId, 'proxy').pipe(first()).toPromise()
        const { orientation, mediaWithoutOrientation } = extractAndRemoveExifOrientationFromMedia(media.dataUrl)

        const img = new Image();
        const imgSize: { w, h } = await (new Promise((resolve, reject) => {
            img.onload = () => {
                resolve({ w: img.width, h: img.height })
            }
            img.onerror = reject

            if(orientation !== -1) {
                img.src = BASE_64_JPEG_START + bytesToBase64(new Uint8Array(mediaWithoutOrientation as ArrayBuffer))
            } else {
                img.src = media.dataUrl;
            }
        }))


        // Drawing bg image
        const imgConfig = {
            height: imgSize.h,
            image: img,
            width: imgSize.w,
            x: 0,
            y: 0,
        }
        const fixedConfig = await this.fixImageOrientation(imgConfig, orientation)
        this.hiddenStageShapesLayer.add(new Konva.Image(fixedConfig))

        for(const [index, alteration] of alterations.entries()) {
            // Retrieving drawing data
            const svg = JSON.parse(alteration.drawing)

            let firstShape = true
            for(const shapeSvg of svg) {
                if(shapeSvg) {
                    const drawnShape = this.getShapeFromSvg(shapeSvg)
                    this.hiddenStageShapesLayer.add(drawnShape)

                    if(firstShape) {
                        // add number label
                        const labelRadius = ConditionReportPDFGenerator.ANNOTATION_LABEL_RADIUS_RATIO * imgConfig.width;
                        const labelCenter = getLabelCoordinates(shapeSvg, labelRadius)
                        const labelNode = new Konva.Circle({
                            fill: "white",
                            radius: labelRadius,
                            stroke: "black",
                            strokeWidth: ConditionReportPDFGenerator.ANNOTATION_LABEL_STROKE_WIDTH_RATIO * imgConfig.width,
                            x: labelCenter.x,
                            y: labelCenter.y,
                        })

                        /* About the positionning of text inside the circle:
                         * The top of the text start at x and the left of the text start at y,
                         * so we put the text inside an invisible square of side labelRadius * 2 (= diameter of the circle)
                         * then we use align and vertical align to center the text inside the square
                         * we dont have to forget to make the square coordinate start at the begining of square diameter (so x/y = center - radius) 
                        */
                        const fontSize = ConditionReportPDFGenerator.ANNOTATION_LABEL_FONT_SIZE_RATIO * imgConfig.width
                        const yText = labelCenter.y - labelRadius
                        const xText = labelCenter.x - labelRadius
                        const textNode = new Konva.Text({
                            width: labelRadius * 2,
                            height: labelRadius * 2,
                            align: 'center',
                            verticalAlign: 'middle',
                            x: xText,
                            y: yText,
                            fill: 'black',
                            fontFamily: 'Calibri',
                            fontSize: fontSize,
                            offsetX: 0,
                            offsetY: 0,
                            text: (index+1).toString(),
                        });

                        this.hiddenStageAnnotationsLayer.add(labelNode);
                        this.hiddenStageAnnotationsLayer.add(textNode);
                        firstShape = false
                    }
                }
            }
        }

        this.hiddenStageShapesLayer.draw();
        this.hiddenStageAnnotationsLayer.draw();
        const pixelRatio = this.getPixelRatio(imgConfig.width, imgConfig.height)
        const generatedImgDataUrl = this.hiddenStage.toDataURL({
            pixelRatio
        })
        const lineY = doc.y;

        const pageWidth = ConditionReportPDFGenerator.A4_PAPER_SIZE[0] - (2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN)
        const availableWidth = pageWidth - ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE
        const imageMargin = availableWidth/2

        doc.x += imageMargin

        // Drawing image on pdf
        doc.rect(doc.x, doc.y, ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE, ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE).fill("lightgray");
        doc.image(generatedImgDataUrl, doc.x, doc.y, {
            align: 'center',
            fit: [ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE, ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE],
            valign: 'center'
        })

        doc.y = lineY;

        // If line overflows vertically, go on a new page
        if (doc.y + ConditionReportPDFGenerator.ALTERATION_IMAGE_SIZE > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
            doc.addPage()
            doc.x = imageMargin;
            doc.y = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
        }
        // reset color for next steps
        doc.fill('black')
    }

    private  async drawAnnotations(doc, annotations, displayMode: AlterationsDisplayMode, annotationPathElems?: any) {
        let shouldGoToNewLine = true;
        if (annotations && annotations.length) {
            let annotationIndex = 0;
            for (const annotation of annotations) {
                shouldGoToNewLine = true
                if (annotation.media) {
                    this.resetHiddenCanvases()

                    // Retrieving image data
                    const mediaId = annotation.media;
                    const media = await ImageTransferHelper.getInstance().downloadImage(mediaId, 'proxy').pipe(first()).toPromise()
                    const img = new Image();
                    const imgSize: { w, h } = await (new Promise((resolve, reject) => {
                        img.onload = () => {
                            resolve({ w: img.width, h: img.height })
                        }
                        img.onerror = reject
                        img.src = media.dataUrl;
                    }))

                    // Retrieving drawing data
                    const svg = JSON.parse(annotation.svg);

                    const { orientation, mediaWithoutOrientation } = extractAndRemoveExifOrientationFromMedia(media.dataUrl)
                    if(orientation !== -1) {
                        const test = bytesToBase64(new Uint8Array(mediaWithoutOrientation as ArrayBuffer))
                        img.src = bytesToBase64(new Uint8Array(mediaWithoutOrientation as ArrayBuffer))
                    }

                    // Drawing bg image
                    const imgConfig = {
                        height: imgSize.h,
                        image: img,
                        width: imgSize.w,
                        x: 0,
                        y: 0,
                    }
                    const fixedConfig = await this.fixImageOrientation(imgConfig, orientation)
                    this.hiddenStageShapesLayer.add(new Konva.Image(fixedConfig))

                    // Drawing all shapes with labels
                    let index: number = 1;
                    for (const shapeSvg of svg) {
                        const drawnShape = this.getShapeFromSvg(shapeSvg);
                        if (shapeSvg) {
                            this.hiddenStageShapesLayer.add(drawnShape);
                            const labelRadius = ConditionReportPDFGenerator.ANNOTATION_LABEL_RADIUS_RATIO * imgConfig.width;
                            const labelCenter = getLabelCoordinates(shapeSvg, labelRadius)
                            const labelNode = new Konva.Circle({
                                fill: "white",
                                radius: labelRadius,
                                stroke: "black",
                                strokeWidth: ConditionReportPDFGenerator.ANNOTATION_LABEL_STROKE_WIDTH_RATIO * imgConfig.width,
                                x: labelCenter.x,
                                y: labelCenter.y,
                            })

                            const textNode = new Konva.Text({
                                align: 'left',
                                fill: 'black',
                                fontFamily: 'Calibri',
                                fontSize: ConditionReportPDFGenerator.ANNOTATION_LABEL_FONT_SIZE_RATIO * imgConfig.width,
                                offsetX: 0,
                                offsetY: 0,
                                text: (index++).toString(),
                                x: labelCenter.x,
                                y: labelCenter.y,
                            });

                            this.hiddenStageAnnotationsLayer.add(labelNode);
                            this.hiddenStageAnnotationsLayer.add(textNode);

                            // centering label in circle
                            textNode.offsetX(textNode.width() / 2)
                            textNode.offsetY(textNode.height() / 2)
                        }
                    }
                    this.hiddenStageShapesLayer.draw();
                    this.hiddenStageAnnotationsLayer.draw();
                    const pixelRatio = this.getPixelRatio(imgConfig.width, imgConfig.height)
                    const generatedImgDataUrl = this.hiddenStage.toDataURL({
                        pixelRatio
                    })
                    const lineY = doc.y;

                    // Drawing image on pdf
                    switch (displayMode) {
                        case AlterationsDisplayMode.WITH_THUMBNAILS:
                            doc.rect(doc.x, doc.y, ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE, ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE).fill("lightgray");
                            doc.image(generatedImgDataUrl, doc.x, doc.y, {
                                align: 'center',
                                fit: [ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE, ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE],
                                valign: 'center'
                            })
                            doc.x += ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE + ConditionReportPDFGenerator.IMAGE_BETWEEN_GAP;
                            doc.y = lineY;
                            // If line overflows on the right, go on a new line
                            if (doc.x + ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE > doc.page.width - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
                                doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN
                                doc.y += ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE + ConditionReportPDFGenerator.IMAGE_BETWEEN_GAP
                                shouldGoToNewLine = false;
                            }

                            // If line overflows vertically, go on a new page
                            if (doc.y + ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE > doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN) {
                                doc.addPage()
                                doc.x = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                                doc.y = ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                                shouldGoToNewLine = false;
                            }
                            break;

                        case AlterationsDisplayMode.FULL_PAGE:
                            doc.addPage()
                            doc.sectionTitle(this.$t('pdfDocument.alterationsSection.detailedAlteration'))
                            doc.moveDown()

                            // Alteration path
                            if (annotationPathElems.elementSummary) {
                                doc.boldText(`${annotationPathElems.elementSummary} > `, { continued: true })
                            }
                            if (annotationPathElems.typeOfObs) {
                                doc.regularText(`${annotationPathElems.typeOfObs} > `, { continued: true })
                            }
                            if (annotationPathElems.materialName) {
                                doc.regularText(`${annotationPathElems.materialName} > `, { continued: true })
                            }
                            if (annotationPathElems.typeOfAlteration) {
                                doc.fill("#f15f22")
                                doc.boldText(`${annotationPathElems.typeOfAlteration}`, { continued: true })
                            }
                            if (annotationPathElems.alterationPrecision) {
                                doc.fill("black")
                                doc.regularText(` (${annotationPathElems.alterationPrecision})`, { continued: false })
                            } else {
                                doc.moveDown()
                            }
                            doc.breakContinue()
                            doc.moveDown()

                            const fpLineY = doc.y
                            const pageWidth = doc.page.width - 2 * ConditionReportPDFGenerator.DOCUMENT_MARGIN;
                            doc.image(generatedImgDataUrl, doc.x, doc.y, {
                                align: 'center',
                                fit: [pageWidth, ConditionReportPDFGenerator.FULL_PAGE_IMAGE_SIZE],
                                valign: 'center'
                            })

                            // Caption
                            doc.y = fpLineY + ConditionReportPDFGenerator.FULL_PAGE_IMAGE_SIZE
                            doc.moveDown(2)
                            if (annotation.title) {
                                doc.regularText(getTranslatedString(annotation.title, ConditionReportPDFGenerator.locale), { align: "center" })
                            }

                            // Comments
                            if (annotation.comments) {
                                // doc.moveDown(2)
                                let commentIndex = 1;
                                for (const comment of annotation.comments) {
                                    doc.fill("black")
                                    doc.boldText(`${this.$t('pdfDocument.alterationsSection.observationLabel')} ${commentIndex} : `, { continued: true })
                                    doc.regularText(getTranslatedString(comment, ConditionReportPDFGenerator.locale), {
                                        continued: false,
                                        paragraphGap: 8,
                                    })
                                    commentIndex++;
                                }
                            }
                            doc.y = 0 // to avoid blank pages
                            break;
                    }
                }
                annotationIndex++;
            }
            if (shouldGoToNewLine) {
                doc.y += ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE;
            }
        }
        else {
            doc.regularText()
        }
    }

    /**
     * Estimates the height of the future annotation block and
     * adds a new page if it doesn't fit in the current one
     * @param alteration 
     * @param doc 
     */
    private  maybeOnANewPage(alteration: DefectAlteration, doc) {
        // Take number if medias into account
        const estimatedHeight = alteration.annotations && alteration.annotations.length ? ConditionReportPDFGenerator.DEFAULT_IMAGE_SIZE * 1.2 : 50;
        const overflowY: boolean = doc.page.height - ConditionReportPDFGenerator.DOCUMENT_MARGIN - doc.y < estimatedHeight;
        if (overflowY) {
            doc.addPage()
        }
    }

    private  shallGenerateAlterationsSection(defectElements: DefectElement[]) {
        if(defectElements) {
            for (const element of defectElements) {
                // If element level is not empty
                if (getTranslatedString(element.element, ConditionReportPDFGenerator.locale)
                    || getTranslatedString(element.precision, ConditionReportPDFGenerator.locale)) {
                    return true;
                }
    
                if(element.observationAlterationDetails) {
                    for (const observation of element.observationAlterationDetails) {
                        // If observations level is not empty
                        if (observation.typeOfObservation) {
                            return true;
                        }
        
                        if(observation.materialAlterations) {
                            for (const material of observation.materialAlterations) {
                                // If material level is not empty
                                if (material.material) {
                                    return true;
                                }
            
                                if(material.alterations) {
                                    for (const alteration of material.alterations) {
                                        // If alterations level is not empty
                                        if (alteration.typeOfAlteration
                                            || getTranslatedString(alteration.precision, ConditionReportPDFGenerator.locale)) {
                                            return true;
                                        }
                                        
                                        if(alteration.annotations) {
                                            for (const annotation of alteration.annotations) {
                                                // If annotation level is not empty
                                                if (annotation.media) {
                                                    return true;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        return false;
    }

    private  getPixelRatio(imgWidth: number, imgHeight: number) {
        const ratio = ConditionReportPDFGenerator.IMAGE_GENERATION_TARGET_PIXEL_NUMBER / (imgWidth * imgHeight);
        return ratio < 1 ? ratio : 1;
    }

    private async fixImageOrientation(baseImageConfig, pOrientation?: number) {
        let orientation = 1
        if(pOrientation) {
            orientation = pOrientation
        } else if(!isImageOrientationAutoManaged()) {
            orientation = await getImageOrientation(baseImageConfig.image)
        }
        switch (orientation) {
            case 3: {
                this.hiddenStage.width(baseImageConfig.width)
                this.hiddenStage.height(baseImageConfig.height)

                return {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: baseImageConfig.height,
                    rotation: 180
                }
            }
            case 6: {
                this.hiddenStage.width(baseImageConfig.height)
                this.hiddenStage.height(baseImageConfig.width)

                return {
                    ...baseImageConfig,

                    offsetX: 0,
                    offsetY: baseImageConfig.height,
                    rotation: 90
                }
            }
            case 8: {
                this.hiddenStage.width(baseImageConfig.height)
                this.hiddenStage.height(baseImageConfig.width)

                return {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: 0,
                    rotation: -90,
                }
            }
            default:
                this.hiddenStage.width(baseImageConfig.width)
                this.hiddenStage.height(baseImageConfig.height)
                return baseImageConfig;
        }
    }

    private  getShapeFromSvg(shapeSvg) {
        switch (shapeSvg.type) {
            case DrawingToolType.RECTANGLE:
                return new Konva.Rect(shapeSvg);
            case DrawingToolType.CIRCLE:
                return new Konva.Circle(shapeSvg)
            case DrawingToolType.FREE_DRAWING:
                return new Konva.Line(shapeSvg)
            case DrawingToolType.ARROW:
                return new Konva.Arrow(shapeSvg)
            default:
                return new Konva.Circle(shapeSvg)
        }
    }

    private  $t(value: string) {
        //return this.$i18n.t(value, ConditionReportPDFGenerator.locale);
        return _.get(ConditionReportPDFGenerator.I18N_MESSAGES, `${ConditionReportPDFGenerator.locale}.${value}`)
    }

    private drawLineSeparator(doc, spaceFromEdge = 0, linesAboveAndBelow = 0.5) {
        doc.moveDown(linesAboveAndBelow);
      
        doc.moveTo(0 + spaceFromEdge, doc.y)
          .lineTo(doc.page.width - spaceFromEdge, doc.y)
          .stroke();
      
        doc.moveDown(linesAboveAndBelow);
        
        return doc
    }

    private getDateFormatFromServer(): string {
        return localStorage.getItem('dateFormat') ?? DATE_FORMATS.YYYY_MM_DD_DASH
    }
}



